import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { SITE_URL, useSeo } from '../../lib/seo';

interface KatsuojiWork {
  title: string;
  youtubeId: string;
  description?: string;
}

const works: KatsuojiWork[] = [
  {
    title: '勝尾寺 語り 其の一',
    youtubeId: 'ht-KfojF9tg',
    description: '勝尾寺の歴史と自然に包まれた静寂の語り',
  },
  {
    title: '勝尾寺 語り 其の二',
    youtubeId: 'iUGPrdVNaog',
    description: '四季折々の勝尾寺、音で感じる祈りの空間',
  },
  {
    title: '勝尾寺 語り 其の三',
    youtubeId: 'FyFn6IDPxqQ',
    description: '勝尾寺に響く、時を超えた音の記憶',
  },
];

export default function KatsuojiWorksPage() {
  useSeo({
    title: '勝尾寺 語り | 制作実績 | 合同会社WakuWaku',
    description:
      '合同会社WakuWakuの制作実績「勝尾寺 語り」シリーズ。勝尾寺の自然と祈りの空間を音で綴った三部作のナレーション・音響制作。',
    path: '/katsuoji-works/',
    keywords: '勝尾寺,ナレーション制作,音響制作実績,寺院 音響,大阪 箕面,MA制作',
  });

  useEffect(() => {
    const siteUrl = SITE_URL;
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/katsuoji-works/#webpage`,
      url: `${siteUrl}/katsuoji-works/`,
      name: '勝尾寺 語り | 制作実績 | 合同会社WakuWaku',
      description: '合同会社WakuWakuの制作実績「勝尾寺 語り」シリーズ。勝尾寺の自然と祈りの空間を音で綴った三部作。',
      isPartOf: { '@id': `${siteUrl}/#website` },
      inLanguage: 'ja',
      dateModified: '2026-06-28',
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-katsuoji';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('schema-katsuoji');
      if (el) document.head.removeChild(el);
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      <main className="flex-grow">
        <section className="relative min-h-[320px] flex items-center justify-center overflow-hidden bg-gradient-to-b from-gray-900 to-gray-800">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(20,184,166,0.12),transparent_70%)]"></div>
          <div className="relative z-10 container mx-auto px-4 text-center py-20">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white/80 text-sm font-semibold px-4 py-1.5 rounded-full mb-6 border border-white/20">
              <i className="ri-film-line"></i>
              <span>制作実績</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-3" style={{ fontFamily: '"Noto Serif JP", serif' }}>
              勝尾寺 語り
            </h1>
            <p className="text-white/60 text-base max-w-lg mx-auto leading-relaxed">
              箕面の山深くに佇む勝尾寺。その静寂と祈りの空間を、音で紡いだ三部作です。
            </p>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {works.map((work) => (
                  <div key={work.youtubeId} className="bg-gray-50 rounded-xl overflow-hidden border border-gray-100 hover:border-teal-200 transition-all group">
                    <div className="relative bg-black" style={{ aspectRatio: '16/9' }}>
                      <iframe
                        src={`https://www.youtube.com/embed/${work.youtubeId}`}
                        title={work.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute inset-0 w-full h-full"
                      ></iframe>
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-bold text-gray-800 mb-1">{work.title}</h3>
                      {work.description && (
                        <p className="text-gray-500 text-sm leading-relaxed">{work.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-16 text-center">
                <p className="text-gray-400 text-sm">
                  <i className="ri-information-line mr-1"></i>
                  全 {works.length} 作品を表示中
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}